import { AuditLogChangeType } from '@lmc/ts-common'
import { MultiSelect, RangePicker, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { reportingConstants } from 'locale/reporting'
import { useTranslation } from 'react-i18next'
import { useCarers } from 'screens/Organisation/screens/TeamList'
import styled from 'styled-components'
import { type AuditTrailFilters, capitalize } from '../utils/utils'

type FiltersProps = {
  value: Partial<AuditTrailFilters>
  onChange: (value: Partial<AuditTrailFilters>) => void
  setCurrentPage: (page: number) => void
}

const AuditFilters = ({ value, onChange, setCurrentPage }: FiltersProps) => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const { carers } = useCarers()

  const teamMembersOptions = carers?.map((teamMember) => ({
    label: `${teamMember.name.first} ${teamMember.name.last}`,
    value: teamMember.id
  }))

  const eventCategoryOptions = Object.entries(AuditLogChangeType).map(
    ([_, value]) => ({
      label: <Text>{capitalize(value)}</Text>,
      value
    })
  )

  const handleInputChange = (newValue: Partial<AuditTrailFilters>) => {
    const updatedValue: AuditTrailFilters = {
      ...value,
      ...newValue
    }
    onChange(updatedValue)
    setCurrentPage(1)
  }

  return (
    <Wrapper>
      <StyledRangePicker
        value={value.rangePicker}
        onChange={(rangePicker) => handleInputChange({ rangePicker })}
        placeholder={[t(reportingConstants.FROM), t(reportingConstants.TO)]}
      />
      <AuthorMultiSelect
        title={t(reportingConstants.AUTHOR)}
        options={teamMembersOptions || []}
        onChange={(selectedMembers: unknown[]) =>
          handleInputChange({ agentId: selectedMembers as unknown as string[] })
        }
      />
      <EventSelect
        placeholder={<Text>{t(reportingConstants.EVENT)}</Text>}
        options={eventCategoryOptions || []}
        allowClear
        onChange={(changeType: unknown) =>
          handleInputChange({
            changeType: changeType as AuditLogChangeType
          })
        }
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  border-radius: 16px;
  padding: 24px;
  background-color: ${({ theme }) => theme.color.neutral.color10};
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-wrap: wrap;
  }
`

const StyledRangePicker = styled(RangePicker)`
  width: 200px;
`

const AuthorMultiSelect = styled(MultiSelect)`
  min-width: 220px;
`

const EventSelect = styled(Select)`
  min-width: 160px;
`

export default AuditFilters
